/**
 * Playlist Sync Service
 *
 * Main entry point for playlist synchronization functionality.
 * Provides a simple API for syncing songs with external music service playlists.
 *
 * Security: Sync operations are handled by the spotify-playlist-sync edge function.
 * Tokens are retrieved and used server-side only, never sent to the client.
 */

import { playlistSyncQueue } from './queue';
import { getPlaylistSyncConfig } from './interface';
import type { PlaylistSyncEventListener, PlaylistSyncConfig } from './types';
import type { MusicService } from '../../types/domain';

/**
 * Sync a song addition to the list's playlist.
 *
 * This should be called after a song is created to add it to the
 * list's synced playlist if the list is connected to a music service.
 *
 * @param userId - User ID (needed for authentication)
 * @param trackId - Music service track ID (e.g., Spotify track ID)
 * @param service - Music service identifier (from the list)
 * @param playlistId - Playlist ID in the music service (from the list)
 * @returns Operation ID if queued, null if not configured
 */
export async function syncSongAdded(
  userId: string,
  trackId: string | null,
  service: MusicService | null,
  playlistId: string | null
): Promise<string | null> {
  // No track ID means song wasn't from a music service
  if (!trackId || !service || !playlistId) {
    return null;
  }

  // Queue the add operation
  return playlistSyncQueue.enqueue('add', userId, trackId, playlistId, service);
}

/**
 * Sync a song deletion to the list's playlist.
 *
 * This should be called after a song is deleted to optionally remove it
 * from the list's synced playlist based on user settings.
 *
 * @param userId - User ID (needed for authentication and settings check)
 * @param trackId - Music service track ID (e.g., Spotify track ID)
 * @param service - Music service identifier (from the list)
 * @param playlistId - Playlist ID in the music service (from the list)
 * @returns Operation ID if queued, null if not synced
 */
export async function syncSongDeleted(
  userId: string,
  trackId: string | null,
  service: MusicService | null,
  playlistId: string | null
): Promise<string | null> {
  // No track ID means song wasn't from a music service
  if (!trackId || !service || !playlistId) {
    return null;
  }

  // Check user's preference for removing from playlist
  const config = await getPlaylistSyncConfig(userId);
  if (!config.removeFromPlaylistOnDelete) {
    return null;
  }

  // Queue the remove operation
  return playlistSyncQueue.enqueue('remove', userId, trackId, playlistId, service);
}

/**
 * Add a listener for sync events.
 * Useful for showing toast notifications on success/failure.
 */
export function addSyncEventListener(listener: PlaylistSyncEventListener): void {
  playlistSyncQueue.addListener(listener);
}

/**
 * Remove a sync event listener.
 */
export function removeSyncEventListener(listener: PlaylistSyncEventListener): void {
  playlistSyncQueue.removeListener(listener);
}

/**
 * Get the current sync queue status.
 */
export function getSyncQueueStatus(): {
  pending: number;
  processing: number;
  failed: number;
} {
  return playlistSyncQueue.getQueueStatus();
}

/**
 * Clear all failed operations from the queue.
 */
export function clearFailedOperations(): void {
  playlistSyncQueue.clearFailed();
}

/**
 * Retry all failed operations.
 */
export function retryFailedOperations(): void {
  playlistSyncQueue.retryFailed();
}

/**
 * Get the user's playlist sync configuration.
 */
export { getPlaylistSyncConfig };

// Re-export types
export type { PlaylistSyncConfig, PlaylistSyncEventListener };
export type { PlaylistSyncOperation, PlaylistSyncOperationType, PlaylistSyncResult } from './types';
